import { useEffect, useState } from "react"
import { InputModel } from "../../../../../../utils/types"
import { Item } from "../../../../../../utils/types/generals"
import { useDashboardMainContext } from "../../provider"
import { currentWeek } from "../methods/calcDates"
import { Box, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { intervaleList } from "../../../../../../utils/constants/intervaleList"
import { filesManagerServiceFetch } from "../../../../../../utils/constants/callService"
import { getUserRole, getUserId } from "../../../../../../service/auth"
import { Roles } from "../../../../../../utils/enums/roles"

const useInput = () => {
    const { items } = useDashboardMainContext()
    const [input, setInput] = useState<Record<string, InputModel>>({})
    const [managers, setManagers] = useState<Item[]>([])
    const [supervisors, setSupervisors] = useState<Item[]>([])
    const [hierarchyUsers, setHierarchyUsers] = useState<Item[]>([])
    const { t } = useTranslation()
    const userRole = getUserRole()
    const userId = getUserId()

    const removeValues = () => {
        setInput(prev => ({
            ...prev,
            from: {
                ...prev.from,
                value: null
            },
            to: {
                ...prev.to,
                value: null
            },
        }))
    }

    useEffect(() => {
        const fetchInitialData = async () => {
            if (userRole === Roles.ADMIN) {
                try {
                    const response = await filesManagerServiceFetch.getUsersToAddPermissions({
                        query: 'by_hierarchy=false&role_ids=5'
                    })
                    
                    if (response && Array.isArray(response)) {
                        const managersData = response.map((user: any) => ({
                            label: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                            value: user.id
                        }))
                        setManagers(managersData)
                    }
                } catch (error) {
                    console.error('Error fetching managers:', error)
                }
            } else if (userRole === Roles.GERENT) {
                try {
                    const response = await filesManagerServiceFetch.getDirectChildrens({ userIds: userId.toString() })
                    
                    if (response && Array.isArray(response) && !response.errors) {
                        const supervisorsData = response.map((user: any) => ({
                            label: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                            value: user.id
                        }))
                        setSupervisors(supervisorsData)
                    }
                } catch (error) {
                    console.error('Error fetching supervisors for manager:', error)
                }
            } else if (userRole === Roles.SUPERVISOR) {
                try {
                    const response = await filesManagerServiceFetch.getDirectChildrens({ userIds: userId.toString() })
                    
                    if (response && Array.isArray(response) && !response.errors) {
                        const hierarchyData = response.map((user: any) => ({
                            label: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                            value: user.id
                        }))
                        setHierarchyUsers(hierarchyData)
                    }
                } catch (error) {
                    console.error('Error fetching hierarchy users for supervisor:', error)
                }
            }
        }

        fetchInitialData()
    }, [userRole, userId])

    const fetchSupervisors = async (gerentIds: number[]) => {
        if (gerentIds.length === 0) {
            setSupervisors([])
            setHierarchyUsers([])
            setInput(prev => ({
                ...prev,
                supervisor: { ...prev.supervisor, value: null },
                hierarchyUsers: { ...prev.hierarchyUsers, value: null }
            }))
            return
        }

        try {
            const userIds = gerentIds.join(',')
            const response = await filesManagerServiceFetch.getDirectChildrens({ userIds })
            
            if (response && Array.isArray(response) && !response.errors) {
                const supervisorsData = response.map((user: any) => ({
                    label: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                    value: user.id
                }))
                setSupervisors(supervisorsData)
            } else {
                setSupervisors([])
            }
            
            setInput(prev => ({
                ...prev,
                supervisor: { ...prev.supervisor, value: null },
                hierarchyUsers: { ...prev.hierarchyUsers, value: null }
            }))
        } catch (error) {
            console.error('Error fetching supervisors:', error)
            setSupervisors([])
        }
    }

    const fetchHierarchyUsers = async (supervisorIds: number[]) => {
        if (supervisorIds.length === 0) {
            setHierarchyUsers([])
            setInput(prev => ({
                ...prev,
                hierarchyUsers: { ...prev.hierarchyUsers, value: null }
            }))
            return
        }

        try {
            const userIds = supervisorIds.join(',')
            const response = await filesManagerServiceFetch.getDirectChildrens({ userIds })
            
            if (response && Array.isArray(response) && !response.errors) {
                const hierarchyData = response.map((user: any) => ({
                    label: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                    value: user.id
                }))
                setHierarchyUsers(hierarchyData)
            } else {
                setHierarchyUsers([])
            }
            
            setInput(prev => ({
                ...prev,
                hierarchyUsers: { ...prev.hierarchyUsers, value: null }
            }))
        } catch (error) {
            console.error('Error fetching hierarchy users:', error)
            setHierarchyUsers([])
        }
    }

    useEffect(() => {
        const unitsArray = Array.isArray(items.unit) ? items.unit : (items.unit as any)?.items || []
        const questionnaireArray = Array.isArray(items.questionnaire) ? items.questionnaire : (items.questionnaire as any)?.items || []
        
        setInput(prev => ({
            intervale: {
            label: 'general.tags.intervale',
            translate: true,
            name: 'intervale',
            type: 'select-search',
            items: intervaleList,
            value: prev.intervale?.value !== undefined ? prev.intervale.value : '1d',
            wrapChildren: ({ children, id }) => {
                return (
                    <Box key={id}>
                        <Typography sx={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#707070',
                            marginBottom: '20px',
                        }}>{t('general.intervale.intervale')}</Typography>

                        {children}
                    </Box>
                )
            }
        },
            from: {
                label: 'general.tags.from',
                translate: true,
                name: 'from',
                type: 'date',
                value: prev.from?.value !== undefined ? prev.from.value : currentWeek().start.getTime(),
                rest: {
                    max: 'to',
                },
                size: {
                    xl: 6,
                    lg: 6,
                    md: 6,
                    sm: 6,
                    xs: 6,
                },
                wrapChildren: ({ children, id }) => {
                    return (
                        <Box key={id}>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#707070',
                                marginBottom: '20px',
                            }}>{t('general.filters')}</Typography>

                            {children}
                        </Box>
                    )
                }
            },
            to: {
                label: 'general.tags.to',
                translate: true,
                name: 'to',
                type: 'date',
                value: prev.to?.value !== undefined ? prev.to.value : currentWeek().end.getTime(),
                rest: {
                    min: 'from',
                },
                size: {
                    xl: 6,
                    lg: 6,
                    md: 6,
                    sm: 6,
                    xs: 6,
                },
                itemChildren: {
                    alignSelf: "end"
                }
            },
            units: {
                label: t('general.tags.unit'),
                translate: true,
                name: 'units',
                type: 'filter-select',
                items: unitsArray,
                value: null,
            },
            questionnaire: {
                label: t('general.tags.questionnaires'),
                translate: true,
                name: 'questionnaire',
                type: 'filter-select',
                items: questionnaireArray,
                value: null,
            },
            ...(userRole === Roles.ADMIN && {
                gerent: {
                    label: t('general.tags.manager'),
                    name: 'gerent',
                    type: 'filter-select',
                    items: managers,
                    value: input.gerent?.value ?? null,
                    onChange: (value: any) => {
                        const selectedIds = Array.isArray(value) ? value.map((v: Item) => v.value) : value ? [value] : []
                        fetchSupervisors(selectedIds)
                    },
                }
            }),
            ...((userRole === Roles.ADMIN || userRole === Roles.GERENT) && {
                supervisor: {
                    label: t('general.tags.supervisor'),
                    name: 'supervisor',
                    type: 'filter-select',
                    items: supervisors,
                    value: input.supervisor?.value ?? null,
                    disabled: supervisors.length === 0,
                    onChange: (value: any) => {
                        const selectedIds = Array.isArray(value) ? value.map((v: Item) => v.value) : value ? [value] : []
                        fetchHierarchyUsers(selectedIds)
                    },
                }
            }),
            ...((userRole === Roles.ADMIN || userRole === Roles.GERENT || userRole === Roles.SUPERVISOR) && {
                hierarchyUsers: {
                    label: t('general.tags.hierachyUsers'),
                    name: 'hierarchyUsers',
                    type: 'filter-select',
                    items: hierarchyUsers,
                    value: input.hierarchyUsers?.value ?? null,
                    disabled: hierarchyUsers.length === 0,
                }
            }),
        }))
    }, [items, managers, supervisors, hierarchyUsers, t, userRole])

    return {
        input,
        removeValues,
        items
    }
}

export { useInput }
